<template>
  <div class="phone-outer" ref="phone" v-scrollanim>
    <div class="phone">
      <div class="screen-wrapper">       
        <img 
          v-for="i in screens"
          :src="$getImgUrl(`assets/screens/screen${i}.webp`)"
          :class="[
            'screen',
            `screen-${i}`,
            { 
              active: activeScreen === i,
              underlying: isLastStage && i === 1
            }
          ]"
          :ref="`screen-${i}`" 
          :key="i"
          draggable="false"
        />
        <div class="touch-indicator" ref="touch">
          <div class="ripple-1" ref="ripple1"></div>
          <div class="ripple-2" ref="ripple2"></div>
        </div>
      </div>
    </div>
    <div class="disclaimer-wrap">
      <slot name="disclaimer" />
    </div>
  </div>
</template>

<script>
import gsap from 'gsap'
// Flow of Screen Animation
// 1. Home screen - click on message 
// 2. Transition message app IN from ICON

export default {
  data() {
    return {
      screens: 7,
      isLastStage: false,
      activeScreen: 1
    }
  },
  mounted() {
    const scrollObserver = new IntersectionObserver(
      async (entries, observer) => {
        entries.forEach(async (entry) => {
          if(entry.isIntersecting) {
            await this.wait(2500);
            this.cycleAnims();
            observer.unobserve(entry.target);
          }
        });
      }, 
      {
        threshold: 0.5
      }
    );
    scrollObserver.observe(this.$refs.phone);
  },
  methods: {
    wait(ms=750) {
      return new Promise((res)=> {
        return setTimeout(()=> {
          res(true)
        }, ms)
      })
    },
    async cycleAnims() {
      await this.wait()
      await this.showScreen2()
      await this.wait()
      await this.showScreen3()
      await this.wait()
      await this.showScreen4()
      await this.wait()
      await this.showScreen5()
      await this.wait()
      await this.showScreen6()
      await this.wait()
      await this.showScreen7()
      await this.wait(1500)
      await this.showScreen1()
      this.cycleAnims()
    },
    async touchActivate() {
      gsap.fromTo(this.$refs.ripple1, {
        opacity: 1,
        scale: 0,
      }, {
        opacity: 0,
        scale: 1.5,
        duration: 0.5,
        clearProps: 'all'
      })
      await gsap.fromTo(this.$refs.ripple2, {
        opacity: 1,
        scale: 0,
      }, {
        opacity: 0,
        scale: 2,
        duration: 0.5,
        delay: 0.1,
        clearProps: 'all'
      })
    },
    async showScreen2() {
      await gsap.to(this.$refs.touch, {
        y: `${828/36 * 100}%`,
        x: `${164/36 * 100}%`,
        duration: 0.5
      })
      await this.wait(50)
      await this.touchActivate()
      await gsap.fromTo(this.$refs['screen-2'], {
        zIndex: 4,
        top: '87%',
        left: '30%',
        width: `${74/418 * 100}%`,
        height: `${74/928 * 100}%`,
        opacity: 0.5,
      }, {
        top: '0%',
        left: '0%',
        width: '100%',
        height: '100%',
        duration: 0.3,
        opacity: 1,
        clearProps: 'z-index',
        onComplete: ()=> {
          this.activeScreen = 2
        }
      })
    },
    async showScreen3() {
      await gsap.to(this.$refs.touch, {
        y: `${796/36 * 100}%`,
        x: `${360/36 * 100}%`,
        duration: 0.5
      })
      await this.wait(50)
      await this.touchActivate()
      await gsap.fromTo(this.$refs['screen-3'], {
        zIndex: 4,
        opacity: 0,
      }, {
        duration: 0.3,
        opacity: 1,
        clearProps: 'z-index',
        onComplete: ()=> {
          this.activeScreen = 3
        }
      })
    },
    async showScreen4() {
      await gsap.to(this.$refs.touch, {
        y: `${604/36 * 100}%`,
        x: `${360/36 * 100}%`,
        duration: 0.5
      })
      await this.wait(50)
      await this.touchActivate()
      await gsap.fromTo(this.$refs['screen-4'], {
        zIndex: 4,
        y: "20%"
      }, {
        y: "0%",
        duration: 0.3,
        clearProps: 'z-index',
        onComplete: ()=> {
          this.activeScreen = 4
        }
      })
    },
    async showScreen5() {
      await gsap.to(this.$refs.touch, {
        y: `${534/36 * 100}%`,
        x: `${210/36 * 100}%`,
        duration: 0.5
      })
      await this.wait(50)
      await this.touchActivate()
      await gsap.fromTo(this.$refs['screen-5'], {
        zIndex: 4,
      }, {
        duration: 0.3,
        clearProps: 'z-index',
        onComplete: ()=> {
          this.activeScreen = 5
        }
      })
    },
    async showScreen6() {
      await gsap.to(this.$refs.touch, {
        y: `${642/36 * 100}%`,
        x: `${55/36 * 100}%`,
        duration: 0.5
      })
      await this.wait(50)
      await this.touchActivate()
      await gsap.fromTo(this.$refs['screen-6'], {
        zIndex: 4,
        opacity: 0,
      }, {
        duration: 0.3,
        opacity: 1,
        clearProps: 'z-index',
        onComplete: ()=> {
          this.activeScreen = 6
        }
      })
    },
    async showScreen7() {
      await gsap.to(this.$refs.touch, {
        y: `${443/36 * 100}%`,
        x: `${421/36 * 100}%`,
        duration: 0.5
      })
      await this.wait(50)
      await this.touchActivate()
      gsap.to(this.$refs.touch, {
        opacity: 0,
        duration: 0.5
      })
      await gsap.fromTo(this.$refs['screen-7'], {
        zIndex: 4,
      }, {
        duration: 0.3,
        onComplete: ()=> {
          this.activeScreen = 7
        }
      })
    },
    async showScreen1() {
      this.isLastStage = true
      await gsap.fromTo(this.$refs['screen-7'], {
        zIndex: 4,
        top: '0%',
        left: '0%',
        width: '100%',
        height: '100%',
        opacity: 1,
      }, {
        top: '87%',
        left: '30%',
        width: `${74/418 * 100}%`,
        height: `${74/928 * 100}%`,
        opacity: 0.5,
        duration: 0.3,
        clearProps: 'all',
        onComplete: ()=> {
          this.activeScreen = 1
          this.isLastStage = false
        }
      })
      gsap.fromTo(this.$refs['touch'], {
        y: '1336%',
        x: '613%',
        opacity: 0,
      }, {
        opacity: 1,
        duration: 0.5
      })
    },
  }
}
</script>

<style lang="scss" scoped>

.phone-outer {
  top: 0;
  right: 0;
  position: absolute;
  width: 247px;
  height: 481px;
  margin: 0 0 20px;
  &:after {
    position: absolute;
    content: '';
    top: 0;
    left: 100%;
    bottom: 0;
    width: 140px;
    background-image: url('~@/assets/screens/shadow.webp');
    background-repeat: no-repeat;
    background-size: auto 100%;
    background-position: 0 0;
    transform: translate3d(-10%, 0, 0) scaleY(1.015);
    z-index: 1;
  }
  @media screen and (max-width: 600px) {
    position: relative;
    margin: 0 auto 20px;
  }
}
.phone {
  width: 100%;
  height: 100%;
  position: relative;
  background-color: #424854;
  border-radius: 16px;
  padding: 8px 4px 9px;
  z-index: 2;
}


.screen-wrapper {
  position: relative;
  background-color: white;
  width: 100%;
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
}

img.screen {
  position: absolute;
  top: -1px;
  left: -1px;
  width: calc(100% + 2px);
  height: calc(100% + 2px);
  border-radius: 16px;
  z-index: 1;
}

img.screen.underlying {
  z-index: 2;
}

img.screen.active {
  z-index: 3;
}

.touch-indicator {
  width: 18px;
  height: 18px;
  background-color: rgba(59, 124, 243, 0.6);
  border-radius: 50%;
  z-index: 10;
  position: absolute;
  top: 0;
  left: 0;
  overflow: visible;
  transform: translate3d(
    613%,
    1336%,
    0
  );
  .ripple-1,
  .ripple-2 {
    position: absolute;
    top: 0;
    left: 0;
    width: 18px;
    height: 18px;
    background-color: rgba(59, 124, 243, 0.75);
    border-radius: 50%;
    opacity: 0;
  }
}

.phone-outer.before-enter {
  transform: translate3d(0, 30px, 0);
  opacity: 0;
  .disclaimer-wrap,
  &:after {
    opacity: 0;
  }
  &:after {
    transform: translate3d(-15%, 0, 0) scaleY(0.95);
  }
}
.phone-outer.enter {
  transform: translate3d(0, 0, 0);
  opacity: 1;
  transition: 1s transform, 1s opacity;
  transition-delay: 1s;
  .disclaimer-wrap {
    opacity: 1;
    transition: 500ms transform, 500ms opacity;
    transition-delay: 2s;
  }
  &:after {
    opacity: 1;
    transform: translate3d(-10%, 0, 0) scaleY(1.015);
    transition: 1s transform, 1s opacity;
    transition-delay: 2.5s;
  }
}


</style>